import { useMemo, useState } from 'react'

import _ from 'lodash'
import { LoaderCircle } from 'lucide-react'

import { useBorderColor } from '../../hooks/useBorderColor'
import { LevelFooter } from '../Footer'
import { HUD } from '../HUD'
import { LevelToolbar } from '../Toolbar'

import { useLevelContext } from '@/features/level-play'
import { useFlow } from '@/shared/lib/steps-flow'
import { ChallengeStep } from '@/shared/lib/steps-flow/types'
import { showErrorAlert } from '@/shared/lib/ui'
import { Button } from '@/shared/ui/Button'


interface GameThisOrThatProps {
  step: ChallengeStep
  challengeId: number
}

export function GameThisOrThat({ challengeId }: GameThisOrThatProps) {
  const {
    state,
    interactionDisabled,
    goNext,
    enableContinue,
    verifyChallenge,
    retryChallenge
  } = useFlow()

  const { level, journeyAllowWrongAnswers, isSubmitting, onChallengeAnswer } =
    useLevelContext()
  const challenge = level?.active_challenges.find(
    challenge => challenge.id === challengeId
  )
  const { meta } = challenge ?? {}
  const associations = useMemo(() => {
    const options =
      (challenge?.challenge_options?.length
        ? challenge.challenge_options.map(opt => ({
            name: opt.name ?? (opt as { text?: string }).text ?? '',
            is_correct: opt.meta?.is_correct ?? false,
            image: opt.meta?.image
          }))
        : meta?.associations) ?? []
    return _.shuffle(options)
  }, [challenge?.challenge_options, meta?.associations])

  const [selectedAssociation, setSelectedAssociation] = useState(null)

  const { getBorderColor } = useBorderColor()

  const toggleSelect = (association: any) => {
    if (isSubmitting || interactionDisabled) return
    if (selectedAssociation !== association) setSelectedAssociation(association)
  }

  const handleVerify = async () => {
    const skipValidation = challenge?.meta?.save_answer
    const correctAssociation = associations.find(
      (a): a is { name: string; is_correct: boolean; image?: unknown } =>
        Boolean((a as { is_correct?: boolean })?.is_correct)
    )
    // If no option is marked as correct, the challenge is misconfigured.
    // Treat it like skipValidation so the student is not stuck.
    const noCorrectConfigured = !correctAssociation
    const answerIsCorrect = noCorrectConfigured
      ? false
      : selectedAssociation?.name === correctAssociation?.name
    const canAnswerChallenge =
      answerIsCorrect || skipValidation || noCorrectConfigured || journeyAllowWrongAnswers

    try {
      if (canAnswerChallenge) {
        const { ok } = await onChallengeAnswer({
          uid: challenge.uid,
          answer: selectedAssociation?.name,
          answerIsCorrect: answerIsCorrect || noCorrectConfigured,
          skipValidation: skipValidation || noCorrectConfigured
        })

        if (ok) enableContinue()
      }

      verifyChallenge({ correct: answerIsCorrect || skipValidation || noCorrectConfigured })
    } catch (error) {
      showErrorAlert(error)
    }
  }

  const handleRetry = async () => {
    setSelectedAssociation(null)
    retryChallenge()
  }

  return (
    <HUD.Card>
      <HUD.Header challenge={challenge} />
      <HUD.Toolbar>
        <LevelToolbar />
      </HUD.Toolbar>
      <HUD.Body className="flex flex-col gap-4">
        <HUD.Description challenge={challenge} />

        <div className="grid gap-2 w-full flex-grow mx-auto grid-cols-2">
          {associations.map((association: any, index: number) => {
            const isSelected = selectedAssociation === association
            const borderColor = getBorderColor({
              hasSelection: true,
              isSelected
            })

            return (
              <div
                key={`association-${index}`}
                className={`
              border-3 rounded-xl cursor-pointer transition-colors duration-200
              ${borderColor} bg-white
              hover:shadow-md
            `}
                onClick={() => toggleSelect(association)}
                tabIndex={0}
                role="button"
                aria-pressed={isSelected}
              >
                {association?.image ? (
                  <div className="flex flex-col items-center">
                    <div className="aspect-square w-full rounded-t-xl overflow-hidden">
                      <img
                        src={_.get(association, 'image.file.url')}
                        className="object-cover object-center bg-white w-full h-full"
                        style={{ pointerEvents: 'none' }}
                        alt=""
                      />
                    </div>
                    <span
                      className="text-gray-800 font-bold text-base md:text-lg w-full text-center block p-4"
                      style={{
                        wordBreak: 'break-word'
                      }}
                    >
                      {_.get(association, 'name')}
                    </span>
                  </div>
                ) : (
                  <span
                    className="text-gray-800 font-bold text-base md:text-lg w-full text-center  p-4 min-h-[100px] h-full flex flex-col items-center justify-center"
                    style={{
                      wordBreak: 'break-word'
                    }}
                  >
                    {_.get(association, 'name')}
                  </span>
                )}
              </div>
            )
          })}
        </div>
      </HUD.Body>
      <LevelFooter>
        {state.phase == 'READY' && (
          <Button
            type="button"
            onClick={handleVerify}
            className="w-full font-bold btn btn-info position-relative z-index-3"
            disabled={!selectedAssociation || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <LoaderCircle className="size-5 animate-spin" />{' '}
                <span>Carregando</span>
              </>
            ) : (
              <span>Verificar</span>
            )}
          </Button>
        )}

        {state.phase === 'CORRECT' && (
          <Button
            type="button"
            onClick={goNext}
            className="w-full font-bold btn btn-info position-relative z-index-3"
            disabled={isSubmitting}
          >
            <span>Continuar</span>
          </Button>
        )}

        {state.phase === 'LOCKED_WRONG' && (
          <Button
            type="button"
            onClick={journeyAllowWrongAnswers ? goNext : handleRetry}
            className="w-full font-bold btn btn-info position-relative z-index-3"
            disabled={isSubmitting}
          >
            <span>
              {journeyAllowWrongAnswers ? 'Entendido' : 'Tentar novamente'}
            </span>
          </Button>
        )}
      </LevelFooter>
    </HUD.Card>
  )
}
